// Per-row equivalent of legacy `applyIndemnityPayoutToPhase`. Indemnity
// policies pay a fixed monthly benefit regardless of cost; excess payout above
// eligible cost accrues as surplus and is drained on later shortfall months —
// UNLESS the policy is exhausted, in which case the reserve FREEZES (see below).
//
// Two branches (one or the other taken per row — never both):
//   1. The policy is NOT exhausted AND openSurplus > 0 AND the payout falls
//      short of eligible cost → drain surplus into the shortfall against the
//      row's *full* cost (not just the eligible portion), capped at openSurplus.
//      This is the "deficit while the policy is still live" case the reserve
//      exists to cover — an above-cap month, or a row the policy declined to pay
//      while it still has drawable benefit left.
//   2. otherwise → accrue new surplus = max(payout − eligibleCost, 0)
//
// EXHAUSTION FREEZE: once the policy can never pay again — STC benefit-period
// exhaustion OR lifetime-max (dollar pool) exhaustion — the reserve must FREEZE,
// not keep bleeding into post-exhaustion out-of-pocket cost (which would
// understate OOP and overstate coverage long after the policy is spent).
// Exhaustion is the caller's `policyExhausted` flag, derived from REAL pool/
// period state — NOT inferred from a $0 payout. A $0-payout row can still be
// live: a 0-benefit care environment (`getDailyBenefitAmountForEnvironment`
// returns 0 for an uncovered setting), or cmTwo holding the pool back to fund a
// cheaper environment later — there the reserve must still drain. A frozen row
// takes branch 2 with payout 0 → surplusAccrued 0 and surplusApplied 0, so
// `closeSurplus = openSurplus` and the per-row continuity identity holds. The
// boundary row that EXHAUSTS the policy mid-row still had drawable benefit at
// open (it pays a partial benefit), so it drains; the freeze begins on the first
// fully-spent row after it.
//
// Reused across strategies for indemnity products (CM II first; Hybrid
// indemnity variants will plug in the same helper).

export interface ApplyIndemnitySurplusInput {
  openSurplus: number;
  indemnityPayout: number;
  // Dollars-equivalent of the row's benefit-eligible cost portion
  // (eligible days × inflated daily cost).
  eligibleCost: number;
  // Dollars-equivalent of the row's *full* effective care cost. Surplus
  // drains against this — legacy precedent: `phaseInflatedProfessionalShareCost`.
  fullCost: number;
  // True once the policy can never pay again — its drawable pool(s) (and, for
  // STC, its benefit period) are spent. Caller derives it from real pool/period
  // state, NOT from a $0 payout (a $0-payout row can still be live — see header).
  // When true the reserve freezes; when false it drains into any shortfall.
  policyExhausted: boolean;
}

export interface ApplyIndemnitySurplusResult {
  // Direct cost-coverage from this row's payout (before surplus drain).
  // Maps to legacy `phaseCostCoverage` per row → `rows[].flows.ltcCoveragePaid`.
  ltcCoveragePaid: number;
  // Total dollars-applied-to-cost (direct + drained surplus). Maps to
  // legacy `costCoverageAddedToTotal` per row → `rows[].flows.ltcPayoutToCost`.
  ltcPayoutToCost: number;
  surplusApplied: number;
  surplusAccrued: number;
  closeSurplus: number;
}

export function applyIndemnitySurplus(
  input: ApplyIndemnitySurplusInput,
): ApplyIndemnitySurplusResult {
  const { openSurplus, indemnityPayout, eligibleCost, fullCost, policyExhausted } = input;
  const ltcCoveragePaid = Math.min(eligibleCost, indemnityPayout);
  const shortfall = eligibleCost - ltcCoveragePaid;

  let surplusApplied = 0;
  let surplusAccrued = 0;
  let ltcPayoutToCost = ltcCoveragePaid;

  if (!policyExhausted && openSurplus > 0 && shortfall > 0) {
    const remainingCostToCover = Math.max(fullCost - ltcCoveragePaid, 0);
    surplusApplied = Math.min(openSurplus, remainingCostToCover);
    ltcPayoutToCost = ltcCoveragePaid + surplusApplied;
  } else {
    surplusAccrued = Math.max(indemnityPayout - ltcCoveragePaid, 0);
  }

  return {
    ltcCoveragePaid,
    ltcPayoutToCost,
    surplusApplied,
    surplusAccrued,
    closeSurplus: openSurplus + surplusAccrued - surplusApplied,
  };
}
